import { Space, Steps, Typography } from "antd";
import moment from "moment";
import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { ContentWrapper } from "../../../Components/Layout/ContentWrapper";
import { ProjectInformation, TemplateOption, validateProjectInformation } from "./Steps/ProjectInformation";
import { SOR } from "./Steps/SOR";
import { ProjectDocuments, blankLink } from "./Steps/ProjectDocuments";
import { Reviewers, blankReviewer } from "./Steps/Reviewers";
import { ReviewSubmit } from "./Steps/ReviewSubmit";

import {
  ApproverEntry,
  DocumentLinkEntry,
  ROUTING_ROLE,
  ReviewerEntry,
  SorItem,
  WORK_ORDER_STEPS,
  WorkOrderFormValues,
  nextDocumentId,
  recordToFormValues,
  recordToLinks,
  recordToSorItems,
  workOrderInitialValues,
  workOrderStepIcons,
  workOrderDocuments,
  workOrderStepTitles,
  REVIEWER_ORDER,
  approverOrder,
  linksPayload,
} from "../Constants";
import {
  WorkflowReviewer,
  buildWorkOrderBody,
  templateApi,
  toArray,
  workOrderApi,
  workflowApi,
} from "../../../Services";
import { useNotification } from "../../../Hooks/useNotification";
import { findApproverGroup } from "../../ApprovalManagement/Constants";
import styles from "../WorkOrder.module.scss";

/**
 * The envelopeId a create/draft-create response carries, wherever it sits.
 *
 * `unwrap` already hands back `response.data`. V4's /work-order/create and
 * draft-create answer with the id itself as that string (as e-POD's create
 * does) — read as an object it came back empty, so the picked documents were
 * never uploaded. Other routes answer with the record or a bare
 * `{ envelopeId }`, so every shape is tried.
 */
const createdEnvelopeId = (result: any): string =>
  String(
    (typeof result === "string" ? result : undefined) ??
    result?.envelopeId ??
    result?.workOrder?.envelopeId ??
    result?.data?.envelopeId ??
    result?.records?.[0]?.envelopeId ??
    ""
  ).trim();

/**
 * Add Work Order — 5-step wizard shell.
 *
 * Mirrors the stepper pattern from AddNewSPA (antd <Steps> driven by a
 * `currentStep` state, `items` built from a title/icon list, vertical label
 * placement) but keeps each step in its own file rather than one 5k-line
 * component, so the steps stay independently readable and testable.
 *
 * The wizard owns all cross-step state — `formValues` for the Project
 * Information fields and `sorItems` for the SOR table — and passes it down.
 * That keeps each step presentational and makes the final Review & Submit
 * step a pure read of what the user has entered.
 */
export const AddWorkOrder = () => {
  const navigate = useNavigate();
  /* Editing an existing work order: /workorder/add?envelopeId=… */
  const envelopeId = new URLSearchParams(window.location.search).get("envelopeId") ?? "";
  /* The record this wizard writes to: the reopened draft's id, or the one
     draft-create hands back the first time Save as Draft is used here.

     Save as Draft used to POST /work-order/draft-create unconditionally, so
     saving again from a later step wrote a SECOND work order — everything
     entered on SOR, Project Documents and Reviewers went into that duplicate
     and the draft the user had opened still had none of it. */
  const [workOrderId, setWorkOrderId] = useState(envelopeId);
  const [currentStep, setCurrentStep] = useState(WORK_ORDER_STEPS.PROJECT_INFORMATION);
  /* The opened record's status. Anything but DRAFT has been submitted. */
  const [savedStatus, setSavedStatus] = useState("");
  const isSubmitted = !!savedStatus && savedStatus !== "DRAFT";
  const [formValues, setFormValues] = useState<WorkOrderFormValues>({
    ...workOrderInitialValues,
    /* A new work order is dated today; the field is not editable. */
    ...(!envelopeId && { dateRequest: moment().format("YYYY-MM-DD") }),
  });
  const [sorItems, setSorItems] = useState<SorItem[]>([]);
  /* Files picked on step 3. create / draft-create take no binary, so — as in
     e-POD — they are uploaded once the record exists (see uploadPicked). */
  const [documents, setDocuments] = useState<File[]>([]);
  /* The opened record, for the documents it already holds: new uploads take
     the next ids and its current version. */
  const [savedRecord, setSavedRecord] = useState<any>(null);
  /* Documents a reopened draft already holds — current-version, read under the
     draft's "-draft" id — so Project Documents' Next counts them and new
     uploads number on from their ids instead of starting at 100 again. */
  const [savedDocuments, setSavedDocuments] = useState<any[]>([]);
  const savedDocumentCount = savedDocuments.length;
  useEffect(() => {
    if (!envelopeId) return;
    const isDraft = savedStatus === "DRAFT" || /-draft$/i.test(envelopeId);
    if (isDraft) return;
    workOrderApi
      .currentDocuments(envelopeId, isDraft)
      .then((data: any) => {
        const rows = toArray(data);
        if (rows.length) {
          const docs = workOrderDocuments({ documentVersionResponses: rows });
          if (docs.length) {
            setSavedDocuments((prev) => (prev.length ? prev : docs));
          }
        }
      })
      .catch(() => undefined);
  }, [envelopeId, savedStatus]);
  const [documentLinks, setDocumentLinks] = useState<DocumentLinkEntry[]>([blankLink(0)]);
  /* Nothing picked by default: the user chooses the Approval Authority. */
  const [authority, setAuthority] = useState<string>("");
  const [reviewers, setReviewers] = useState<ReviewerEntry[]>([blankReviewer(0)]);
  const [approvers, setApprovers] = useState<ApproverEntry[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const { openNotification } = useNotification();

  /**
   * Editing, including opening a draft: load the work order itself.
   *
   * Only the routing was being read back, so Edit opened an empty wizard and a
   * draft lost everything that had been typed into it. The record comes back
   * shaped like WorkOrderRequestDto — `division` for Cost Centre, `category`
   * for the contract category, `sorDetails` for the SOR lines — so each field
   * is read under both that name and the one the form uses.
   */
  useEffect(() => {
    if (!envelopeId) return;

    workOrderApi
      .detail(envelopeId)
      .then((payload: any) => {
        /* The response wraps the record: `response.data.workOrder` holds the
           fields and `response.data.reviewers` sits BESIDE it, not inside. A
           draft opened blank because the mapping below read the wrapper, which
           has none of these fields on it. */
        const record: any = Array.isArray(payload)
          ? payload[0]
          : payload?.records?.[0] ??
          payload?.workOrder ??
          payload?.workOrderDetails ??
          payload?.detail ??
          payload;

        if (!record || !Object.keys(record).length) return;
        setSavedStatus(String(record.status ?? "").toUpperCase());
        const docVer = payload?.documentVersion ?? record?.documentVersion;
        const draftDocs = workOrderDocuments({
          ...payload,
          ...record,
          documentVersion: docVer,
          documentVersionResponses: record?.documentVersionResponses ?? payload?.documentVersionResponses,
        });
        setSavedRecord({
          ...record,
          documentVersion: docVer,
          documentVersionResponses: record.documentVersionResponses ?? payload?.documentVersionResponses,
          lastVersion: record.lastVersion ?? payload?.lastVersion,
        });
        if (draftDocs.length) {
          setSavedDocuments((prev) => (prev.length ? prev : draftDocs));
        }
        const text = (...candidates: any[]) =>
          String(candidates.find((value) => value !== undefined && value !== null) ?? "").trim();

        /* Requester and Date Request are required on step 1 but are not part
           of WorkOrderRequestDto, so a reopened draft came back without them
           and could not be stepped past. The requester is whoever is signed
           in, and the request date is when the record was created. */
        const signedIn = (() => {
          try {
            const panelUser = JSON.parse(localStorage.getItem("panelUser") || "{}");
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            return {
              name: panelUser?.panelUserName || panelUser?.userName || user?.userName || "",
              email: panelUser?.email || user?.email || "",
            };
          } catch {
            return { name: "", email: "" };
          }
        })();

        setFormValues((current) => recordToFormValues(record, current, signedIn));

        const lines = recordToSorItems(record);
        if (lines.length) setSorItems(lines);

        const links = recordToLinks(record);
        if (links.length) setDocumentLinks(links);

        /* Saved records carry the API's name for the authority (MDGCEO), the
           radio group is keyed on ours (mdGceo). */
        const savedAuthority = findApproverGroup(record.approvalAuthority);
        if (savedAuthority) setAuthority(savedAuthority.key);

        /* A draft stores its chain on the work order, under whichever reviewer
           spelling the endpoint that wrote it used. The workflow service is
           asked as well (below) and wins when it has rows of its own. */
        const routing = toArray<any>(
          payload?.reviewers ?? record.reviewers ?? record.routing
        );
        if (routing.length) {
          const isApprover = (row: any) =>
            String(row.role ?? "").toUpperCase().includes(ROUTING_ROLE.APPROVER);
          const nameOf = (row: any) => text(row.userName, row.name, row.reviewerName);
          const emailOf = (row: any) => text(row.userEmail, row.email);

          const loadedReviewers = routing.filter((row) => !isApprover(row));
          if (loadedReviewers.length) {
            setReviewers(
              loadedReviewers.map((row, index) => ({
                key: `reviewer-${index}`,
                name: nameOf(row),
                email: emailOf(row),
              }))
            );
          }

          const loadedApprovers = routing.filter(isApprover);
          if (loadedApprovers.length) {
            setApprovers(
              loadedApprovers.map((row, index) => ({
                key: `approver-${index}`,
                designation: text(row.designation),
                name: nameOf(row),
                email: emailOf(row),
              }))
            );
          }
        }
      })
      .catch(() => undefined); // keep the blank wizard; api.service reports it
  }, [envelopeId]);

  /* Editing: the routing already exists, so it is read back from the workflow
     service rather than rebuilt from the authority group. Each row keeps its
     `id` so the submit can update it in place via /workflow/edit-reviewer. */
  useEffect(() => {
    /* Submitted work orders only. A draft's chain is the one Save as Draft
       writes onto the work order, read with the record above; the workflow
       service can still hold the chain the draft was first saved with, and,
       answering after the detail call, it put the previous approvers back. */
    if (!envelopeId || !isSubmitted) return;
    workflowApi
      .reviewers(envelopeId)
      .then((rows: any) => {
        /* A draft keeps its chain on the work order itself rather than in the
           workflow service, so fall back to what the detail call returned. */
        const list = toArray<WorkflowReviewer>(rows);
        if (!list.length) return;
        const isApprover = (row: WorkflowReviewer) =>
          String(row.role ?? "").toUpperCase().includes(ROUTING_ROLE.APPROVER);

        const existingReviewers = list.filter((row) => !isApprover(row));
        if (existingReviewers.length) {
          setReviewers(
            existingReviewers.map((row, index) => ({
              key: `reviewer-${row.id ?? index}`,
              id: row.id,
              name: row.reviewerName ?? "",
              email: row.email ?? "",
            }))
          );
        }

        const existingApprovers = list.filter(isApprover);
        if (existingApprovers.length) {
          setApprovers(
            existingApprovers.map((row, index) => ({
              key: `approver-${row.id ?? index}`,
              id: row.id,
              designation: row.designation ?? "",
              name: row.reviewerName ?? "",
              email: row.email ?? "",
            }))
          );
          const loadedGroup = findApproverGroup(existingApprovers[0]?.authorityGroup);
          if (loadedGroup) setAuthority(loadedGroup.key);
        }
      })
      .catch(() => undefined); // fall back to the blank routing rows
  }, [envelopeId, isSubmitted]);

  /**
   * A new work order starts as the signed-in user's, dated today.
   *
   * Requester Name and Email are no longer typed — they are the session's, and
   * the fields are read-only — so they have to be filled in from here.
   */
  useEffect(() => {
    if (envelopeId) return; // an existing record brings its own requester
    const signedIn = (() => {
      try {
        const panelUser = JSON.parse(localStorage.getItem("panelUser") || "{}");
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        return {
          name: panelUser?.panelUserName || panelUser?.userName || user?.userName || "",
          email: panelUser?.email || user?.email || "",
        };
      } catch {
        return { name: "", email: "" };
      }
    })();

    /* Date Request is left empty: pre-filling it with today put a date on the
       work order that nobody chose, and one that is simply wrong whenever the
       request was raised on another day. */
    setFormValues((current) => ({
      ...current,
      requesterName: current.requesterName || signedIn.name,
      requesterEmail: current.requesterEmail || signedIn.email,
    }));
  }, [envelopeId]);

  /* Templates are fetched once here rather than in each step, so the picker
     and the Review read-back resolve names from the same list. */
  useEffect(() => {
    templateApi
      .getAll()
      .then((rows: any) =>
        setTemplates(
          toArray(rows).map((row: any) => ({
            templateId: String(row.templateId ?? row.id ?? ""),
            // Catalog names come back padded; trim once at the source.
            templateName: String(row.templateName ?? "").trim(),
          }))
        )
      )
      .catch(() => setTemplates([]));
  }, []);

  /* The Template picker was taken off step 1, but `templateId` is still part
     of the payload, so the first configured template is used. */
  useEffect(() => {
    if (!templates.length) return;
    setFormValues((current) =>
      current.templateId ? current : { ...current, templateId: templates[0].templateId }
    );
  }, [templates]);

  /**
   * Everything the wizard collected, in the shape WorkOrderRequestDto takes.
   *
   * The DTO has no `approvers` property and no `draft` flag: reviewers and
   * approvers travel as one list told apart by `role`, and draft versus final
   * is the endpoint being called rather than a field. Document links go under
   * `documentLinkDetails`, and `templateId` belongs INSIDE the body.
   *
   * The chain is handed over as `routing` in one neutral shape; create and
   * draft-create name its fields differently, so workOrder.api renames it per
   * endpoint (see RoutingEntry).
   */
  const collectPayload = (values: WorkOrderFormValues = formValues) => {
    /* A blank reviewer row is not a step in the chain. Numbering counts the
       rows that survive that filter, so the orders stay 1..n with no gap —
       they used to start the approvers at `reviewers.length + 1`, which left a
       hole whenever an empty row was dropped. */
    /* BOTH halves, not either. A row with a name and no address was being sent
       with `email: ""`, and the server rejects the whole request over it —
       "Reviewer email '' must belong to domain ..." — so the draft was never
       written and never appeared in the list. The same goes for the approver
       rows, which were sent wholesale: a designation with nobody configured
       for it carries an empty address. */
    const isFilled = (row: { name?: string; email?: string }) =>
      Boolean(String(row.name ?? "").trim() && String(row.email ?? "").trim());

    const filledReviewers = reviewers.filter(isFilled);
    const filledApprovers = approvers.filter(isFilled);

    return {
      ...buildWorkOrderBody(values, sorItems),
      templateName:
        templates.find((template) => template.templateId === values.templateId)?.templateName ??
        "",
      approvalAuthority: findApproverGroup(authority)?.authorityGroup ?? authority,
      routing: [
        ...filledReviewers.map((reviewer, index) => ({
          name: reviewer.name,
          email: reviewer.email,
          designation: "",
          order: REVIEWER_ORDER,
          role: ROUTING_ROLE.REVIEWER,
          authorityGroup: findApproverGroup(authority)?.authorityGroup ?? authority,
        })),
        ...filledApprovers.map((approver, index, list) => ({
          name: approver.name,
          email: approver.email,
          designation: approver.designation,
          order: approverOrder(list, index),
          role: ROUTING_ROLE.APPROVER,
          authorityGroup: findApproverGroup(authority)?.authorityGroup ?? authority,
        })),
      ],
      documentLinkDetails: linksPayload(documentLinks),
    };
  };

  /**
   * V3 has a dedicated draft route, so draft no longer piggybacks on create.
   *
   * Step 1 keeps its fields in Formik and only lifts them on Next, so saving
   * from there would write the values as they were before the step was
   * touched. It passes what is on screen instead, which is committed to the
   * wizard first so the draft and the form agree.
   */
  /**
   * Step 3's files, once the record exists — e-POD's handleUpload: one request
   * each to POST /document/panel/upload, ids from 100 (or on from what the record holds),
   * `draft` while it is one; the backend assigns the version. A failed upload is
   * reported but does not undo the save.
   */
  const getNextDocId = () =>
    nextDocumentId(
      savedDocuments.length
        ? savedDocuments
        : {
          documentVersion: savedRecord?.documentVersion,
          documentVersionResponses: savedRecord?.documentVersionResponses,
        }
    );

  /**
   * Save as draft:
   * 1. Call document-lock API FIRST (with draft: true)
   * 2. Call draft-create the first time, /work-order/edit after that
   * 3. Upload picked documents (draft: true)
   */
  const handleSaveDraft = async (pending?: Partial<WorkOrderFormValues>) => {
    const values = pending ? { ...formValues, ...pending } : formValues;
    if (pending) setFormValues(values);

    setIsSavingDraft(true);
    const payload = collectPayload(values) as any;
    const targetId = (workOrderId || envelopeId || uuidv4()).replace(/-draft$/i, "");
    if (!workOrderId) setWorkOrderId(targetId);
    const versionKey = Math.floor(Math.random() * 1_000_000_000).toString();

    try {
      // Step 1: Call document lock API first (with draft: true)
      await workOrderApi.documentLock({
        envelopeId: targetId,
        versionOrder: 1,
        versionKey,
        draft: true,
      });

      // Step 2: Call draft-create the first time, /work-order/edit after that
      /* Only the first save of a new work order goes to draft-create.
         Saving again goes through /work-order/edit: a draft's reviewers are
         held on the work order, so they travel in that body; a submitted work
         order's routing is on the workflow service. */
      const result = !workOrderId
        ? await workOrderApi.createDraft({ ...payload, envelopeId: targetId })
        : !isSubmitted
          ? await workOrderApi.edit({ ...payload, envelopeId: workOrderId }, true)
          : await workOrderApi
            .edit({ ...payload, envelopeId: workOrderId })
            .then((res) => {
              const routing = collectRouting(workOrderId);
              return routing.length
                ? workflowApi.editReviewers(routing).then(() => res)
                : res;
            });

      const created = createdEnvelopeId(result);
      if (created) setWorkOrderId(created);
      const targetEnvelopeId = (created || targetId).replace(/-draft$/i, "");

      // Step 3: Upload documents
      if (documents.length > 0) {
        await workOrderApi.uploadDocuments({
          envelopeId: targetEnvelopeId,
          files: documents,
          firstDocumentId: getNextDocId(),
          draft: true,
        });
        setDocuments([]);
      }
      openNotification({
        title: "Draft saved",
        message: "The work order has been saved as a draft.",
        type: "success",
      });
      navigate("/workorder");
    } catch (err: any) {
      openNotification({ title: "Failed to save draft", message: err?.message, type: "error" });
    } finally {
      setIsSavingDraft(false);
    }
  };

  /**
   * The routing in the shape `PUT /workflow/edit-reviewer` takes.
   *
   * Reviewers and approvers share one list there, told apart by `role`, and
   * `routingOrder`: every reviewer is 1, the approvers one step per
   * designation — all R1 2, R2 3, … (approverOrder).
   */
  const collectRouting = (id: string = workOrderId): WorkflowReviewer[] => {
    /* Complete rows only — see collectPayload. */
    const complete = (row: { name?: string; email?: string }) =>
      Boolean(String(row.name ?? "").trim() && String(row.email ?? "").trim());
    const filled = reviewers.filter(complete);
    const common = {
      envelopeId: id,
      subsidiary: formValues.subsidiary,
      division: formValues.costCentre,
      authorityGroup: findApproverGroup(authority)?.authorityGroup ?? authority,
      budget: formValues.budgetStatus,
    };
    return [
      ...filled.map((reviewer, index) => ({
        ...common,
        id: reviewer.id,
        reviewerName: reviewer.name,
        email: reviewer.email,
        designation: "",
        routingOrder: REVIEWER_ORDER,
        role: ROUTING_ROLE.REVIEWER,
      })),
      ...approvers.filter(complete).map((approver, index, list) => ({
        ...common,
        id: approver.id,
        reviewerName: approver.name,
        email: approver.email,
        designation: approver.designation,
        routingOrder: approverOrder(list, index),
        role: ROUTING_ROLE.APPROVER,
      })),
    ];
  };

  /* Set when Submit finds Project Information incomplete: that step reopens
     with every missing field marked. */
  const [showStepErrors, setShowStepErrors] = useState(false);

  /**
   * Submit / Onboarding — mirrors SPA's onSubmit:
   * - If existing draft (submitting draft) or editing:
   *   1. Submit / edit work order (create with envelopeId / edit)
   *   2. Upload documents (draft: false)
   *   3. Draft transfer (workOrderApi.draftTransfer - PUT)
   * - If fresh creation:
   *   1. Create work order first to get envelopeId (create)
   *   2. Upload documents (draft: false)
   */

  const handleSubmit = async () => {
    /* Next lets Project Information be left incomplete; the final Submit is
       where its mandatory fields are enforced. */
    const missing = validateProjectInformation(formValues);
    if (Object.keys(missing).length) {
      openNotification({
        title: "Please fill in all mandatory fields",
        message: `Project Information: ${Object.values(missing).join(", ")}.`,
        type: "error",
      });
      setShowStepErrors(true);
      setCurrentStep(WORK_ORDER_STEPS.PROJECT_INFORMATION);
      return;
    }
    setIsSubmitting(true);

    const existingId = (workOrderId || envelopeId || "").replace(/-draft$/i, "");
    const payload = collectPayload() as any;

    try {
      if (existingId) {
        // Submitting an existing draft / record: follow SPA onSubmit (if envelopId)
        // Step 1: Submit / edit work order
        const result =
          workOrderId && isSubmitted
            ? await workOrderApi
              .edit({ ...payload, envelopeId: workOrderId })
              .then((res) => workflowApi.editReviewers(collectRouting()).then(() => res))
            : await workOrderApi.create({
              ...payload,
              envelopeId: existingId,
            });

        const created = createdEnvelopeId(result);
        const targetEnvelopeId = (created || existingId).replace(/-draft$/i, "");
        if (created) setWorkOrderId(created);

        // Step 2: Upload documents
        if (documents.length > 0) {
          await workOrderApi.uploadDocuments({
            envelopeId: targetEnvelopeId,
            files: documents,
            firstDocumentId: getNextDocId(),
            draft: false,
          });
          setDocuments([]);
        }

        // Step 3: Draft transfer (transfer from draft to permanent store)
        const isDraftSubmit =
          (!isSubmitted && Boolean(existingId)) ||
          savedStatus === "DRAFT" ||
          savedDocuments.length > 0;

        if (isDraftSubmit && targetEnvelopeId) {
          try {
            await workOrderApi.draftTransfer(targetEnvelopeId);
          } catch (transferErr: any) {
            console.error("Draft transfer failed:", transferErr);
          }
        }
      } else {
        // Fresh creation: follow SPA onSubmit (else)
        // Step 1: Create document first
        const result = await workOrderApi.create(payload);
        const newEnvelopeId = (createdEnvelopeId(result) || "").replace(/-draft$/i, "");
        if (newEnvelopeId) setWorkOrderId(newEnvelopeId);

        // Step 2: Upload documents
        if (newEnvelopeId && documents.length > 0) {
          await workOrderApi.uploadDocuments({
            envelopeId: newEnvelopeId,
            files: documents,
            firstDocumentId: 100,
            draft: false,
          });
          setDocuments([]);
        }
      }

      openNotification({
        title: isSubmitted ? "Work Order updated" : "Work Order created",
        message: "The work order has been submitted.",
        type: "success",
      });
      navigate("/workorder");
    } catch (err: any) {
      openNotification({
        title: isSubmitted ? "Failed to update work order" : "Failed to create work order",
        message: err?.message,
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /* Every step opens at its top — Next, Back and Review & Submit's pencils
     alike. The page scrolls inside the content card, which kept its position
     across steps and left the new one half scrolled. */
  useEffect(() => {
    document.querySelector("#contentWrapperContainer > .ant-card-body")?.scrollTo({ top: 0 });
  }, [currentStep]);

  const next = () => setCurrentStep((step) => Math.min(step + 1, WORK_ORDER_STEPS.REVIEW_SUBMIT));
  const back = () =>
    setCurrentStep((step) => Math.max(step - 1, WORK_ORDER_STEPS.PROJECT_INFORMATION));

  const stepItems = workOrderStepTitles.map((title: string, index: number) => ({
    key: index,
    title,
    icon: (
      <Icon icon={currentStep > index ? "charm:tick" : workOrderStepIcons[index]} />
    ),
  }));

  return (
    <ContentWrapper
      customTitle={
        <Space align="start">
          <Icon
            onClick={() => navigate("/workorder")}
            icon="akar-icons:arrow-left"
            className={styles.backIcon}
          />
          <span>
            <Typography.Title
              level={3}
              style={{ margin: 0 }}
            >
              {envelopeId ? "Edit Work Order" : "Add Work Order"}
            </Typography.Title>
          </span>
        </Space>
      }
    >
      <div className={styles.stepperWrapper}>
        <Steps
          current={currentStep}
          items={stepItems}
          labelPlacement="vertical"
          size="default"
          responsive
        />
      </div>

      <div style={{ marginTop: 24 }}>
        {currentStep === WORK_ORDER_STEPS.PROJECT_INFORMATION && (
          <ProjectInformation
            values={formValues}
            templates={templates}
            onNext={(values: WorkOrderFormValues) => {
              setFormValues(values);
              next();
            }}
            onSaveDraft={handleSaveDraft}
            isSavingDraft={isSavingDraft}
            lockOrgFields={isSubmitted}
            lockDateRequest={!envelopeId}
            showErrors={showStepErrors}
          />
        )}

        {currentStep === WORK_ORDER_STEPS.SOR && (
          <SOR
            items={sorItems}
            setItems={setSorItems}
            category={formValues.contractCategoryName || formValues.contractCategory}
            region={formValues.regionName || formValues.region}
            onBack={back}
            onNext={next}
            onSaveDraft={handleSaveDraft}
            isSavingDraft={isSavingDraft}
          />
        )}

        {currentStep === WORK_ORDER_STEPS.PROJECT_DOCUMENTS && (
          <ProjectDocuments
            files={documents}
            setFiles={setDocuments}
            savedDocuments={savedDocuments}
            setSavedDocuments={setSavedDocuments}
            savedCount={Math.max(savedDocumentCount, workOrderDocuments(savedRecord).length)}
            links={documentLinks}
            setLinks={setDocumentLinks}
            onBack={back}
            onNext={next}
            onSaveDraft={handleSaveDraft}
            isSavingDraft={isSavingDraft}
          />
        )}

        {currentStep === WORK_ORDER_STEPS.APPROVERS && (
          <Reviewers
            values={formValues}
            authority={authority}
            setAuthority={setAuthority}
            reviewers={reviewers}
            setReviewers={setReviewers}
            approvers={approvers}
            setApprovers={setApprovers}
            onBack={back}
            onNext={next}
            onSaveDraft={handleSaveDraft}
            isSavingDraft={isSavingDraft}
            keepSavedRouting={isSubmitted}
          />
        )}

        {currentStep === WORK_ORDER_STEPS.REVIEW_SUBMIT && (
          <ReviewSubmit
            values={formValues}
            templates={templates}
            items={sorItems}
            files={documents}
            savedDocuments={savedDocuments}
            links={documentLinks}
            reviewers={reviewers}
            approvers={approvers}
            authority={authority}
            onBack={back}
            onEditStep={setCurrentStep}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
            onSaveDraft={handleSaveDraft}
            isSavingDraft={isSavingDraft}
          />
        )}
      </div>
    </ContentWrapper>
  );
};
